import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Bell, AlertTriangle, Info, ShieldAlert, XCircle, CheckCheck } from 'lucide-react';

import { api } from '../services/api';
import { toastInfo } from '../stores/toast';
import { useAuth } from '../stores/auth';
import { adminUiCopy } from '../i18n/adminUi';
import { useI18n } from '../i18n/provider';

const UNREAD_KEY = ['notifications-unread-count'];
const RECENT_KEY = ['notifications-recent'];

function timeAgo(dateStr: string): string {
  const seconds = Math.floor((Date.now() - new Date(dateStr).getTime()) / 1000);
  if (seconds < 60) return 'just now';
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

function SeverityIcon({ severity }: { severity: string }): JSX.Element {
  switch (severity) {
    case 'critical':
      return <ShieldAlert className="w-4 h-4 text-red-600 shrink-0" />;
    case 'error':
      return <XCircle className="w-4 h-4 text-red-500 shrink-0" />;
    case 'warning':
      return <AlertTriangle className="w-4 h-4 text-amber-500 shrink-0" />;
    default:
      return <Info className="w-4 h-4 text-blue-500 shrink-0" />;
  }
}

export function NotificationBell(): JSX.Element {
  const { user } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { locale } = useI18n();
  const copy = adminUiCopy[locale].notifications;
  const [isOpen, setIsOpen] = useState(false);
  const panelRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const previousUnreadCountRef = useRef<number | null>(null);

  // Poll unread count every 60 seconds
  const { data: unreadData } = useQuery({
    queryKey: UNREAD_KEY,
    queryFn: () => api.notificationsUnreadCount(),
    enabled: !!user,
    refetchInterval: 60_000,
    staleTime: 30_000,
  });

  // Fetch recent notifications when dropdown opens
  const { data: recentData } = useQuery({
    queryKey: RECENT_KEY,
    queryFn: () => api.listNotifications({ limit: 8 }),
    enabled: !!user && isOpen,
    staleTime: 15_000,
  });

  const markRead = useMutation({
    mutationFn: (id: string) => api.markNotificationRead(id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: UNREAD_KEY });
      void queryClient.invalidateQueries({ queryKey: RECENT_KEY });
    },
  });

  const markAllRead = useMutation({
    mutationFn: () => api.markAllNotificationsRead(),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: UNREAD_KEY });
      void queryClient.invalidateQueries({ queryKey: RECENT_KEY });
    },
  });

  const count = unreadData?.count || 0;
  const notifications = recentData?.data || [];

  // Surface a subtle, professional toast only when unread count increases.
  useEffect(() => {
    const prev = previousUnreadCountRef.current;
    if (prev !== null && count > prev && !isOpen) {
      const delta = count - prev;
      const message =
        delta === 1
          ? copy.toastNewSingle
          : copy.toastNewMultiple.replace('{count}', String(delta));
      toastInfo(message, copy.bellRecentTitle);
    }
    previousUnreadCountRef.current = count;
  }, [count, isOpen, copy.toastNewSingle, copy.toastNewMultiple, copy.bellRecentTitle]);

  // Close on outside click
  useEffect(() => {
    if (!isOpen) return;
    function handleClick(e: MouseEvent) {
      if (
        panelRef.current &&
        !panelRef.current.contains(e.target as Node) &&
        buttonRef.current &&
        !buttonRef.current.contains(e.target as Node)
      ) {
        setIsOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [isOpen]);

  function handleNotificationClick(n: (typeof notifications)[0]) {
    if (!n.isRead) {
      markRead.mutate(n.id);
    }
    setIsOpen(false);
    if (n.actionUrl) {
      navigate(n.actionUrl);
    }
  }

  return (
    <div className="relative">
      <button
        ref={buttonRef}
        onClick={() => setIsOpen(!isOpen)}
        className={`relative inline-flex h-10 w-10 items-center justify-center rounded-xl transition-colors ${
          count > 0
            ? 'text-blue-600 bg-blue-50 hover:bg-blue-100'
            : 'text-slate-500 hover:text-slate-700 hover:bg-slate-100'
        }`}
        aria-label="Notifications"
      >
        <Bell className="w-5 h-5" />
        {count > 0 && (
          <>
            <span className="absolute -top-0.5 -right-0.5 h-2.5 w-2.5 rounded-full bg-red-500/60 animate-ping" />
            <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] flex items-center justify-center bg-red-500 text-white text-[10px] font-bold rounded-full px-1 leading-none">
              {count > 9 ? '9+' : count}
            </span>
          </>
        )}
      </button>

      {isOpen && (
        <>
          {/* Mobile: full-screen overlay */}
          <div className="fixed inset-0 z-40 md:hidden bg-black/20" onClick={() => setIsOpen(false)} />
          <div
            ref={panelRef}
            className="fixed inset-x-4 top-16 z-50 md:absolute md:inset-auto md:right-0 md:top-full md:mt-2 md:w-96 bg-white rounded-xl shadow-lg border border-slate-200 overflow-hidden"
          >
            {/* Header */}
            <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100">
              <h3 className="text-sm font-semibold text-slate-900">
                {copy.bellRecentTitle}
              </h3>
              {count > 0 && (
                <button
                  onClick={() => markAllRead.mutate()}
                  disabled={markAllRead.isPending}
                  className="flex items-center gap-1 text-xs text-blue-600 hover:text-blue-700 disabled:opacity-50"
                >
                  <CheckCheck className="w-3.5 h-3.5" />
                  {copy.markAllRead}
                </button>
              )}
            </div>

            {/* Notification list */}
            <div className="max-h-[400px] overflow-y-auto divide-y divide-slate-50">
              {notifications.length === 0 ? (
                <div className="px-4 py-8 text-center text-sm text-slate-400">
                  {copy.bellNoNotifications}
                </div>
              ) : (
                notifications.map((n) => (
                  <button
                    key={n.id}
                    onClick={() => handleNotificationClick(n)}
                    className={`w-full text-left px-4 py-3 hover:bg-slate-50 transition-colors flex gap-3 ${
                      !n.isRead ? 'bg-blue-50/40' : ''
                    }`}
                  >
                    <SeverityIcon severity={n.severity} />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2">
                        <p className={`text-sm leading-snug truncate ${!n.isRead ? 'font-semibold text-slate-900' : 'text-slate-700'}`}>
                          {n.title}
                        </p>
                        {!n.isRead && (
                          <span className="mt-1.5 w-2 h-2 rounded-full bg-blue-500 shrink-0" />
                        )}
                      </div>
                      <p className="text-xs text-slate-500 truncate mt-0.5">{n.message}</p>
                      <p className="text-[11px] text-slate-400 mt-1">{timeAgo(n.createdAt)}</p>
                    </div>
                  </button>
                ))
              )}
            </div>

            {/* Footer */}
            <div className="border-t border-slate-100 px-4 py-2.5">
              <button
                onClick={() => {
                  setIsOpen(false);
                  navigate('/admin/notifications');
                }}
                className="w-full text-center text-xs font-medium text-blue-600 hover:text-blue-700"
              >
                {copy.viewAll}
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
